import { DocumentNode, gql } from '@apollo/client';
import { HStack, StackProps, Text, Tooltip } from '@chakra-ui/react';
import * as React from 'react';

import { LogInSignUpModal } from '@/components/LogInSignUp';
import { PaywallModal } from '@/components/Paywall';
import { useSponsorPaywallModal } from '@/components/Post/hooks/useSponsorPaywallModal';
import { useShowPostResponses } from '@/hooks/useShowPostResponses';
import { getFullTimestamp, getTimeAgoShorthand } from '@/utilities';

import { LikeReplyFooter_CommentFragment } from './graphql-types/LikeReplyFooter_CommentFragment';

import { CommentLikeButton } from './components/CommentLikeButton';
import { LikeCount } from './components/LikeCount';
import { ReplyButton } from './components/ReplyButton';
import { ReplyCount } from './components/ReplyCount';

export const LikeReplyFooter: React.FC<
  {
    comment: LikeReplyFooter_CommentFragment;
    onClickReply: () => void;
    onClickReplyCount?: () => void;
  } & StackProps
> & { commentFragment: DocumentNode } = ({
  comment,
  onClickReply,
  onClickReplyCount,
  ...stackProps
}) => {
  const sponsorPaywallModalProps = useSponsorPaywallModal(comment.postV2);

  const {
    showPostResponses,
    behaviorOverride,
    paywallDisclosure,
    loginDisclosure,
  } = useShowPostResponses(comment.postV2);

  return (
    <>
      <HStack
        justifyContent="space-between"
        alignItems="center"
        width="full"
        {...stackProps}
      >
        <HStack spacing="2" align="center">
          {comment.dateCreated && (
            <Tooltip
              label={getFullTimestamp(new Date(comment.dateCreated))}
              borderRadius="md"
            >
              <Text textStyle="compact" color="gray.600">
                {getTimeAgoShorthand(new Date(comment.dateCreated))}
              </Text>
            </Tooltip>
          )}

          <LikeCount comment={comment} />
          <ReplyCount
            comment={comment}
            onClick={behaviorOverride ? behaviorOverride : onClickReplyCount}
          />
        </HStack>
        <HStack spacing="2" align="center">
          {showPostResponses ? (
            <>
              <ReplyButton
                onClick={behaviorOverride ? behaviorOverride : onClickReply}
              />
              <CommentLikeButton
                comment={comment}
                onClickOverride={behaviorOverride}
              />
            </>
          ) : null}
        </HStack>
      </HStack>
      <PaywallModal
        isOpen={paywallDisclosure.isOpen}
        onClose={paywallDisclosure.onClose}
        source="respond to post paywall"
        {...sponsorPaywallModalProps}
      />
      <LogInSignUpModal
        isOpen={loginDisclosure.isOpen}
        onClose={loginDisclosure.onClose}
      />
    </>
  );
};

LikeReplyFooter.commentFragment = gql`
  fragment LikeReplyFooter_CommentFragment on Comment {
    _id
    dateCreated
    ...CommentLikeButton_CommentFragment
    ...LikeCount_CommentFragment
    ...ReplyCount_CommentFragment
    postV2 {
      _id
      ...useShowPostResponses_PostFragment
      ...useSponsorPaywallModal_PostFragment
    }
  }
  ${CommentLikeButton.commentFragment}
  ${LikeCount.commentFragment}
  ${ReplyCount.commentFragment}
  ${useShowPostResponses.postFragment}
  ${useSponsorPaywallModal.postFragment}
`;
